import React, { useState } from "react";

import { Box, Textarea } from "@chakra-ui/react";
import { closeSubAccount } from "api/business-user-management";
import { ICloseSubAccountPayload } from "api/business-user-management/types";
import { ReactComponent as InfoOutlineIcon } from "assets/icons/info-outline.svg";
import { useUploadDocument } from "hooks/useUploadDocument";
import { useParams } from "react-router-dom";
import { REGEX_VALUES, getUniqueFileName } from "utils";

import { Button } from "ui/button";
import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { FileUpload } from "ui/file-upload";
import { Modal } from "ui/modal";
import { ModalHeader } from "ui/modal/modal-header";
import { defaultModalCss } from "ui/modal/styles";
import { Heading3, Paragraph3 } from "ui/typography";

import {
  DEFAULT_FREEZE_UNFREEZE_FORM_DATA,
  MIN_REASON_LENGTH,
} from "../../consts";
import {
  ICloseAccountModalProps,
  IFreezeUnfreezeBusinessAccountFormData,
} from "./types";

export const CloseAccountModal = (props: ICloseAccountModalProps) => {
  const {
    isOpen,
    setIsOpen,
    handleToggleCloseAccountModal,
    walletId,
    handleGetBusinessAccounts,
  } = props;
  const { businessId } = useParams();
  const { isUploading, uploadFile } = useUploadDocument();
  const [isClosingAccount, setIsClosingAccount] = useState(false);
  const [formData, setFormData] =
    useState<IFreezeUnfreezeBusinessAccountFormData>(
      DEFAULT_FREEZE_UNFREEZE_FORM_DATA
    );

  const handleCloseModal = () => {
    setFormData(DEFAULT_FREEZE_UNFREEZE_FORM_DATA);
    handleToggleCloseAccountModal();
  };

  const handleTextAreaChange = (
    evt: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = evt.target;
    if (!REGEX_VALUES.LETTERS_WITH_NUMBERS_COMMA_AND_HYPHEN_ONLY.test(value)) {
      return;
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (name: string, file: File | null) => {
    setFormData({
      ...formData,
      [name]: file,
    });
  };

  const handleCloseAccount = async () => {
    if (!businessId) {
      console.error("No business id found!");
      return;
    }
    const { reason, attachment } = formData;
    setIsClosingAccount(true);
    const payload: ICloseSubAccountPayload = {
      walletId,
      businessId,
      reason,
    };
    try {
      if (attachment) {
        const {
          status,
          url,
          message: singleFileUploadMsg,
        } = await uploadFile({
          file: attachment as File,
          documentName: "others",
          companyName: getUniqueFileName(
            `${walletId}-${businessId}-close-sub-account-attachment`
          ),
        });
        if (status === "failed") {
          customErrorToast({
            message: singleFileUploadMsg,
          });
          setIsClosingAccount(false);
          return;
        }
        payload["attachment"] = url;
      }
      const { error, message } = await closeSubAccount(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        customSuccessToast({
          message: "Sub Account Closed Successfully",
        });
        await handleGetBusinessAccounts({
          businessId: businessId || "",
        });
        handleCloseModal();
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsClosingAccount(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      modalCss={{
        ...defaultModalCss,
        width: "400px",
        maxHeight: "600px",
        padding: "24px 0",
        gap: "32px",
      }}
      allowOutsideClick={false}
    >
      <Box className="flex flex-col gap-5">
        <ModalHeader
          modalType="rejection"
          modalHeaderCss={{
            marginBottom: 0,
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        />
        <Box className="px-6">
          <Heading3
            fontSize="18px"
            lineHeight="28px"
            textColor="#1A202C"
            fontWeight="600"
          >
            Close Account
          </Heading3>
        </Box>
        <Box className="px-6 flex flex-col gap-5 h-[350px] flex-shrink-0 overflow-x-hidden overflow-y-scroll">
          <Box className="flex flex-col gap-2">
            <Paragraph3
              lineHeight="20px"
              textColor="#475467"
            >
              Please confirm that you want to perform this action.
            </Paragraph3>
            <Box className="bg-[#FEF5E7] border border-[#FFEFD8] rounded-2xl p-3 flex items-center gap-2 h-16">
              <InfoOutlineIcon className="flex-shrink-0" />
              <Paragraph3
                lineHeight="20px"
                textColor=" #1A202C"
              >
                This account will be closed permanently for this business.
              </Paragraph3>
            </Box>
          </Box>
          <Box className="h-[199px] flex flex-col gap-[6px]">
            <label className="text-[#101828] text-sm font-semibold">
              Reason for closure
            </label>
            <Textarea
              css={{
                border: "1px solid #D0D5DD",
                boxShadow: "0px 1px 2px 0px #1018280D",
                borderRadius: "8px",
                height: "173px",
                padding: "10px 16px",
                fontSize: "14px",
                resize: "none",
                "&::placeholder": { color: "#B1B9CA" },
              }}
              placeholder="Enter your reason..."
              name="reason"
              value={formData.reason}
              onChange={handleTextAreaChange}
            />
          </Box>
          <Box>
            <FileUpload
              fileUploadLabel="Attachment (optional)"
              file={formData.attachment}
              name="attachment"
              handleFileChange={handleFileChange}
              doesUploadedFileContainerHaveFullWidth
              fileTypes={[
                "jpeg",
                "jpg",
                "png",
                "pdf",
                "doc",
                "docx",
                "xls",
                "xlsx",
                "csv",
              ]}
              uploadFileEmptyStateContainerCss={{
                height: "118px",
                flexShrink: "0",
              }}
              fileUploadLabelCss={{
                fontSize: "14px",
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box className="flex items-center gap-3 px-6 h-[44px]">
        <Button
          variant="outline"
          textColor="#344054"
          border="1px solid #D0D5DD"
          className="w-[170px]"
          onClick={handleCloseModal}
          disabled={isClosingAccount || isUploading}
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          textColor="#ffffff"
          border="1px solid #00535C"
          className="w-[170px]"
          onClick={handleCloseAccount}
          disabled={
            isClosingAccount ||
            isUploading ||
            formData.reason.length < MIN_REASON_LENGTH
          }
          loading={isClosingAccount || isUploading}
          loadingText="Closing..."
        >
          Confirm
        </Button>
      </Box>
    </Modal>
  );
};
